<script setup lang="ts">
    import { ROUTES } from '@/router';
    import { CASE_SETTINGS_TARGETS } from '@/domain/analytics/analyticsTargets';
    import { useDashboardStore } from '@/views/dashboard/dashboardStore';
    import { onUnmounted } from 'vue';
    import { debounce } from 'lodash';
    import CaseCard from '@/views/dashboard/CaseCard.vue';
    import { useUserStore } from '@/stores/userStore';

    const store = useDashboardStore();
    const user = useUserStore();

    const DEBOUNCE_SEARCH_IN_MILLISECONDS = 150;
    const debounceLoad = debounce(() => store.load(), DEBOUNCE_SEARCH_IN_MILLISECONDS);

    // initial page load without search query or pagination
    store.reset();
    await store.load();

    // Cleanup on unmount
    onUnmounted(() => {
        store.stop();
    });
</script>

<template>
    <v-layout>
        <app-sidebar>
            <router-link :to="!user.isSurgicalSupportEngineer ? { name: ROUTES.NEW_CASE } : ''">
                <app-button-cta
                    width="180"
                    :data-analytics-id="CASE_SETTINGS_TARGETS.newCaseButton"
                    prepend-icon="mdi-plus"
                    :disabled="user.isSurgicalSupportEngineer"
                >
                    Create new Case
                </app-button-cta>
            </router-link>
        </app-sidebar>

        <app-main-view>
            <div class="case-list-header">
                <h2 class="text-secondary-text">
                    Your Formus Cases
                    <span v-if="store.result.count" class="app-case-count">
                        ({{ store.result.count }})
                    </span>
                </h2>

                <div class="case-list-search">
                    <app-text-input
                        v-model="store.searchText"
                        width="500"
                        density="comfortable"
                        rounded
                        class="px-0"
                        append-inner-icon="mdi-magnify"
                        placeholder="Search Cases"
                        bg-color="white"
                        clearable
                        @update:model-value="debounceLoad()"
                    />
                </div>
            </div>

            <div
                v-if="store.result.count > 0"
                class="case-list justify-start"
                data-testid="case-search-results"
            >
                <case-card
                    v-for="item in store.result.cases"
                    :id="item.id"
                    :key="item.id"
                    :title="item.name"
                    :description="item.description"
                    :reference="item.id"
                    :patient="item.patient"
                    :surgeon="item.surgeon"
                    :owner="item.owner"
                    :created-at="item.createdAt"
                    :status="item.status ?? { state: 'unknown', failed: false }"
                />
                <div class="case-pagination">
                    <a v-if="store.result.pages.previous" @click="store.previousPage">previous</a>
                    <span>{{ store.result.pages.current }}</span>
                    <a v-if="store.result.pages.next" @click="store.nextPage">next</a>
                </div>
            </div>
            <div
                v-else-if="store.isReady && store.allCasesSearch && store.result.count === 0"
                data-testid="case-search-results"
            >
                Welcome to Formus Hip. Click the '+ Create new case' button to create your first
                patient case.
            </div>
            <div
                v-else-if="store.isReady && store.result.count === 0"
                data-testid="case-search-results"
            >
                We're sorry, we couldn't find any Cases including "{{ store.searchText }}". Try
                changing your search terms, or clear the search to return to your Cases.
            </div>
        </app-main-view>
    </v-layout>
</template>

<!--suppress CssUnusedSymbol -->
<style scoped lang="scss">
    .case-list {
        max-width: 1020px;
    }

    .case-list-header {
        display: flex;
        justify-content: space-between;
        max-width: 1020px;
    }

    .case-list-search {
        /* flex-basis: 344px; */

        :deep(input) {
            padding-left: 30px;
        }

        :deep(.v-field__append-inner) {
            padding-right: 20px;
        }
    }

    .case-list-header h2 {
        font-size: 1.75rem;
        line-height: 2.875rem;
        font-weight: 400;
        margin: 0 0 2.5rem;
        letter-spacing: 0.0125em;
    }

    .app-case-count {
        color: rgb(var(--v-theme-secondary)) !important;
    }

    .case-pagination {
        display: flex;
        justify-content: center;
        max-width: 1020px;
        margin: 20px;
    }

    .case-pagination a {
        font-size: 12px;
        margin-left: 10px;
        cursor: pointer;
        user-select: none;
    }

    .case-pagination span {
        font-size: 12px;
        margin-left: 10px;
    }
</style>
